import { useState } from 'react';
import axios from 'axios';
import {
  GitDiff, UploadSimple, ArrowClockwise, CheckCircle, Warning, Cloud, HardDrives,
  Trash, Plus, PencilSimple, XCircle, Copy, CaretDown, CaretRight,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BACKEND_META = {
  emergent: { label: 'Emergent', Icon: Cloud, klass: 'text-sky-600' },
  nas:      { label: 'NAS',      Icon: HardDrives, klass: 'text-amber-600' },
};

const fmtBytes = (n) => {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const fmtSignedDelta = (n) => {
  if (n == null) return '';
  const s = n >= 0 ? '+' : '−';
  const abs = Math.abs(n);
  return `${s}${fmtBytes(abs).replace('−', '')}`;
};

/**
 * Manifest Diff Viewer — compares two backup files' storage_manifest blocks
 * and highlights paths that appeared / disappeared / changed size between
 * them. Answers "did last night's cron delete anything by accident?" in one
 * click without pulling either DB payload.
 *
 * Requires backup_version >= 2 on BOTH files (v1 has no manifest). Rejects
 * v1 uploads with a clear error rather than silently producing an empty diff.
 */
export function ManifestDiffCard() {
  const [fileA, setFileA] = useState(null);
  const [fileB, setFileB] = useState(null);
  const [running, setRunning] = useState(false);
  const [diff, setDiff] = useState(null);
  const [error, setError] = useState(null);
  // Which bucket (backend + category) is expanded — key like "emergent:only_in_a"
  const [expanded, setExpanded] = useState({});

  const runDiff = async () => {
    if (!fileA || !fileB) {
      toast.error('両方のバックアップファイルを選択してください');
      return;
    }
    setRunning(true);
    setError(null);
    setDiff(null);
    setExpanded({});
    try {
      const fd = new FormData();
      fd.append('file_a', fileA);
      fd.append('file_b', fileB);
      const r = await axios.post(`${API}/settings/backup/diff-manifests`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setDiff(r.data);
      const { totals } = r.data;
      const changes = (totals.only_in_a || 0) + (totals.only_in_b || 0) + (totals.size_changed || 0);
      if (changes === 0) {
        toast.success('差分はありません — ストレージ内容は完全に一致しています');
      } else {
        toast.warning(`${changes} 件の差分を検出しました`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || '差分計算に失敗しました';
      setError(msg);
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  };

  const toggleExpanded = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const copyPath = async (path) => {
    try { await navigator.clipboard.writeText(path); toast.success('パスをコピーしました'); }
    catch { toast.error('コピーに失敗しました'); }
  };

  const canRun = fileA && fileB && !running;

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="backup-diff-card">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-50 text-violet-700 shrink-0">
          <GitDiff size={20} weight="duotone" />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-bold text-zinc-900">マニフェスト差分</h2>
          <p className="text-[12px] text-zinc-500 mt-0.5 leading-relaxed">
            2 つのバックアップファイルを比較して、<b>ストレージ上に追加・削除・変更されたファイル</b>を検出します。
            深夜の cron で意図しない削除が起きていないかチェックできます。（v2 以降のバックアップが必要）
          </p>
        </div>
      </div>

      {/* Upload row */}
      <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-zinc-200">
        <div>
          <label className="block text-[12px] font-semibold text-zinc-700 mb-1">
            A（古い方 / 基準）
          </label>
          <input
            type="file"
            accept=".json,application/json"
            onChange={(e) => setFileA(e.target.files?.[0] || null)}
            data-testid="backup-diff-file-a"
            className="w-full text-[12px] file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-violet-100 file:text-violet-700 file:font-semibold hover:file:bg-violet-200"
          />
          {fileA && (
            <div className="text-[10px] text-zinc-500 mt-1 truncate" title={fileA.name}>
              {fileA.name} · {fmtBytes(fileA.size)}
            </div>
          )}
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-zinc-700 mb-1">
            B（新しい方 / 比較対象）
          </label>
          <input
            type="file"
            accept=".json,application/json"
            onChange={(e) => setFileB(e.target.files?.[0] || null)}
            data-testid="backup-diff-file-b"
            className="w-full text-[12px] file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-fuchsia-100 file:text-fuchsia-700 file:font-semibold hover:file:bg-fuchsia-200"
          />
          {fileB && (
            <div className="text-[10px] text-zinc-500 mt-1 truncate" title={fileB.name}>
              {fileB.name} · {fmtBytes(fileB.size)}
            </div>
          )}
        </div>
      </div>

      <div className="px-5 py-3 border-b border-zinc-200 flex items-center gap-3">
        <Button
          onClick={runDiff}
          disabled={!canRun}
          data-testid="backup-diff-run"
          className="bg-teal-700 hover:opacity-90 text-white"
        >
          {running ? (
            <><ArrowClockwise size={14} className="mr-1 animate-spin" />比較中…</>
          ) : (
            <><GitDiff size={14} className="mr-1" />差分を計算</>
          )}
        </Button>
        {(fileA || fileB) && !running && (
          <button
            type="button"
            onClick={() => { setFileA(null); setFileB(null); setDiff(null); setError(null); }}
            className="text-[11px] text-zinc-500 hover:text-zinc-800 underline"
            data-testid="backup-diff-reset"
          >
            リセット
          </button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-5 my-3 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-900 flex items-start gap-2" data-testid="backup-diff-error">
          <XCircle size={14} weight="duotone" className="text-rose-600 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Results */}
      {diff && (
        <>
          {/* Summary bar */}
          <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[12px]" data-testid="backup-diff-summary">
            <SummaryTile
              label="削除された"
              value={diff.totals.only_in_a}
              icon={Trash}
              tone={diff.totals.only_in_a > 0 ? 'rose' : 'zinc'}
              testid="backup-diff-deleted-count"
            />
            <SummaryTile
              label="追加された"
              value={diff.totals.only_in_b}
              icon={Plus}
              tone={diff.totals.only_in_b > 0 ? 'emerald' : 'zinc'}
              testid="backup-diff-added-count"
            />
            <SummaryTile
              label="サイズ変更"
              value={diff.totals.size_changed}
              icon={PencilSimple}
              tone={diff.totals.size_changed > 0 ? 'amber' : 'zinc'}
              testid="backup-diff-changed-count"
            />
            <SummaryTile
              label="A ↔ B ファイル数"
              value={`${diff.a.total_files} → ${diff.b.total_files}`}
              icon={CheckCircle}
              tone="zinc"
              testid="backup-diff-total-count"
            />
          </div>

          {/* Meta (export timestamps) */}
          <div className="px-5 py-2 border-b border-zinc-200 text-[11px] text-zinc-500 flex flex-wrap gap-x-4 gap-y-1">
            <span>A: <b>{diff.a.exported_at || '—'}</b> (v{diff.a.backup_version || '?'})</span>
            <span>B: <b>{diff.b.exported_at || '—'}</b> (v{diff.b.backup_version || '?'})</span>
          </div>

          {/* Danger banner when deletions detected */}
          {diff.totals.only_in_a > 0 && (
            <div className="mx-5 my-3 rounded-md bg-rose-50 border-l-4 border-rose-500 px-3 py-2 text-[12px] text-rose-900 flex items-start gap-2" data-testid="backup-diff-deletion-warning">
              <Warning size={14} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
              <span>
                <b>{diff.totals.only_in_a} 件のファイル</b>が A には存在しますが B にはありません。
                意図しない削除の可能性があります。下記の一覧を確認してください。
              </span>
            </div>
          )}

          {/* Per-backend detail */}
          <div className="px-5 py-3 space-y-3" data-testid="backup-diff-backends">
            {Object.entries(diff.backends).map(([name, d]) => {
              const Backend = BACKEND_META[name] || BACKEND_META.emergent;
              if (d.skipped) {
                return (
                  <div key={name} className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900 flex items-start gap-2" data-testid={`backup-diff-backend-skipped-${name}`}>
                    <Warning size={13} weight="duotone" className="text-amber-600 shrink-0 mt-0.5" />
                    <span>
                      <b>{Backend.label}</b> はスキップされました: {d.reason}
                    </span>
                  </div>
                );
              }
              const hasAny = d.only_in_a_count + d.only_in_b_count + d.size_changed_count > 0;
              if (!hasAny) {
                return (
                  <div key={name} className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-900 flex items-center gap-2" data-testid={`backup-diff-backend-clean-${name}`}>
                    <CheckCircle size={13} weight="duotone" className="text-emerald-600" />
                    <span><b>{Backend.label}</b>: 変更なし（<span className="tabular-nums">{d.unchanged_count}</span> ファイル一致）</span>
                  </div>
                );
              }
              return (
                <div key={name} className="rounded-lg border border-zinc-200 overflow-hidden" data-testid={`backup-diff-backend-${name}`}>
                  <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center gap-2 text-[13px] font-semibold text-zinc-900">
                    <Backend.Icon size={14} weight="duotone" className={Backend.klass} />
                    {Backend.label}
                    <span className="text-[11px] text-zinc-500 font-normal ml-auto">
                      未変更 <span className="tabular-nums">{d.unchanged_count}</span> · 変更 <span className="tabular-nums">{d.only_in_a_count + d.only_in_b_count + d.size_changed_count}</span>
                    </span>
                  </div>
                  <DiffBucket
                    label="削除された（A のみ）"
                    color="rose"
                    icon={Trash}
                    count={d.only_in_a_count}
                    items={d.only_in_a}
                    truncated={d.only_in_a_truncated}
                    perBucketCap={diff.per_bucket_cap}
                    expanded={!!expanded[`${name}:only_in_a`]}
                    onToggle={() => toggleExpanded(`${name}:only_in_a`)}
                    onCopy={copyPath}
                    testid={`backup-diff-bucket-${name}-deleted`}
                  />
                  <DiffBucket
                    label="追加された（B のみ）"
                    color="emerald"
                    icon={Plus}
                    count={d.only_in_b_count}
                    items={d.only_in_b}
                    truncated={d.only_in_b_truncated}
                    perBucketCap={diff.per_bucket_cap}
                    expanded={!!expanded[`${name}:only_in_b`]}
                    onToggle={() => toggleExpanded(`${name}:only_in_b`)}
                    onCopy={copyPath}
                    testid={`backup-diff-bucket-${name}-added`}
                  />
                  <DiffBucket
                    label="サイズ変更"
                    color="amber"
                    icon={PencilSimple}
                    count={d.size_changed_count}
                    items={d.size_changed}
                    truncated={d.size_changed_truncated}
                    perBucketCap={diff.per_bucket_cap}
                    expanded={!!expanded[`${name}:size_changed`]}
                    onToggle={() => toggleExpanded(`${name}:size_changed`)}
                    onCopy={copyPath}
                    isSizeChanged
                    testid={`backup-diff-bucket-${name}-changed`}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}


function SummaryTile({ label, value, icon: Icon, tone, testid }) {
  const toneMap = {
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    zinc: 'bg-white border-zinc-200 text-zinc-900',
  };
  const iconToneMap = {
    rose: 'text-rose-600',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    zinc: 'text-zinc-500',
  };
  return (
    <div className={`rounded-lg border px-3 py-2 flex items-center gap-2 ${toneMap[tone]}`} data-testid={testid}>
      <Icon size={16} weight="duotone" className={iconToneMap[tone]} />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
        <div className="font-bold tabular-nums text-[15px] leading-tight">{value}</div>
      </div>
    </div>
  );
}


function DiffBucket({ label, color, icon: Icon, count, items, truncated, perBucketCap, expanded, onToggle, onCopy, isSizeChanged, testid }) {
  if (count === 0) return null;
  const colorMap = {
    rose: 'text-rose-700 bg-rose-50 hover:bg-rose-100 border-rose-100',
    emerald: 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border-emerald-100',
    amber: 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-100',
  };
  const capped = truncated && items.length >= perBucketCap;
  return (
    <div className="border-b border-zinc-200 last:border-b-0" data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center gap-2 px-3 py-2 text-[12px] font-semibold ${colorMap[color]} transition-colors`}
      >
        {expanded ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
        <Icon size={12} weight="duotone" />
        <span>{label}</span>
        <span className="ml-auto tabular-nums bg-white/60 rounded px-1.5 py-0.5">{count}</span>
      </button>
      {expanded && (
        <div className="px-3 py-2 max-h-[240px] overflow-y-auto space-y-0.5" data-testid={`${testid}-list`}>
          {items.map((item) => {
            const path = isSizeChanged ? item.path : item;
            return (
              <div key={path} className="flex items-center gap-2 text-[11px] font-mono text-zinc-700 hover:bg-zinc-50 rounded px-1.5 py-1">
                <button
                  type="button"
                  onClick={() => onCopy(path)}
                  title="パスをコピー"
                  className="text-zinc-400 hover:text-zinc-800 shrink-0"
                >
                  <Copy size={11} />
                </button>
                <span className="truncate flex-1" title={path}>{path}</span>
                {isSizeChanged && (
                  <span className="shrink-0 text-[10px] text-zinc-500 tabular-nums">
                    {fmtBytes(item.size_a)} → {fmtBytes(item.size_b)}
                    <span className={`ml-1 font-bold ${item.delta_bytes > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      ({fmtSignedDelta(item.delta_bytes)})
                    </span>
                  </span>
                )}
              </div>
            );
          })}
          {capped && (
            <div className="text-[11px] text-amber-700 mt-1 px-1.5">
              上位 {perBucketCap} 件のみ表示（さらに多くの差分があります）
            </div>
          )}
        </div>
      )}
    </div>
  );
}
